import MedicationCombobox from '@/components/common/medication-combobox'
import type { MedicationOption } from '@/lib/medication-options'
import { getCurrentUKDate, isTodayInUK } from '@/lib/utils'
import type { Medication } from '@/types/data'
import { format } from 'date-fns'
import { X } from 'lucide-react'
import type { ReactNode } from 'react'
import { DatePicker } from '../datepicker'
import TextareaWithLabel from '../textarea'
import { SetDateIcon, TooltipWrapper } from './previous-orders'

type Props = {
  /** Zero-based. Card 0 is the treatment the med request was raised for. */
  index: number
  isEditable: boolean
  options: MedicationOption[]
  /** Option values rendered as disabled — variants already picked on the sibling cards. */
  disabledValues?: Set<string>
  medication: Medication | null
  direction: string
  /** Omitted on multi product orders: their treatments share the page's single issue date. */
  issueDate?: Date
  onMedicationChange: (hash: string) => void
  onDirectionChange: (direction: string) => void
  onIssueDateChange?: (issueDate: Date) => void
  lastIssuedDate?: Date | null
  onSetAdvancedDate?: () => void
  /** Rendered on the right of the card header, e.g. a treatment's decision actions. */
  headerAction?: ReactNode
  onRemove?: () => void
  /** Locks only the Remove control — set while an approve submit is in flight. */
  isRemoveDisabled?: boolean
}

type IssueDateFieldProps = {
  isEditable: boolean
  issueDate: Date
  onIssueDateChange: (issueDate: Date) => void
  lastIssuedDate?: Date | null
  onSetAdvancedDate?: () => void
  /** Appended to the `medication-issue-date` test id. */
  testIdSuffix?: string
}

export function IssueDateField({
  isEditable,
  issueDate,
  onIssueDateChange,
  lastIssuedDate,
  onSetAdvancedDate,
  testIdSuffix = ''
}: IssueDateFieldProps) {
  return (
    <>
      <p className='text-[#1D1D1D] text-base font-medium leading-[170%] my-4'>Issue Date</p>
      <div
        data-testid={`medication-issue-date${testIdSuffix}`}
        className='rounded-2xl border border-custom-neutral-200 bg-[#EBEFFA] flex px-6 py-4 justify-between items-center'
      >
        <p className='text-[#080F30] text-base font-normal leading-[170%]'>Medication Issue Date</p>

        <div className='flex gap-[26px] items-center'>
          <p className='text-[#080F30] text-base font-semibold leading-[170%]'>
            {format(issueDate, 'd MMMM yyyy')} {isTodayInUK(issueDate) ? '( Today )' : ''}
          </p>

          <DatePicker
            isDisabled={!isEditable}
            fromDate={getCurrentUKDate()}
            date={issueDate}
            setDate={picked => {
              const theDate = new Date(picked ?? new Date())
              // default at noon to prevent timezone date flips
              theDate.setHours(12, 0, 0, 0)
              onIssueDateChange(theDate)
            }}
          />
        </div>
      </div>

      {lastIssuedDate && (
        <div className='flex items-center gap-4 mt-6'>
          <span className='font-poppins text-base leading-7 font-bold text-[#1D1D1D]'>Last ordered date :</span>
          <span className='font-poppins text-base leading-7 text-[#4E545E]'>
            {format(lastIssuedDate, 'd MMMM yyyy')}
          </span>
          {onSetAdvancedDate && (
            <TooltipWrapper
              trigger={
                <button
                  disabled={!isEditable}
                  type='button'
                  className='flex items-center gap-1 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
                  onClick={onSetAdvancedDate}
                >
                  <SetDateIcon />
                </button>
              }
              content={
                <p className='text-white text-xs font-medium leading-4'>Set issue date to 12 weeks from this date</p>
              }
            />
          )}
        </div>
      )}
    </>
  )
}

const CARD_CLASSES =
  'rounded-2xl border border-custom-neutral-200 p-6 shadow-[0px_0px_1px_0px_rgba(113,128,150,0.04),0px_4px_8px_0px_rgba(113,128,150,0.08)]'

export default function TreatmentCard({
  index,
  isEditable,
  options,
  disabledValues,
  medication,
  direction,
  issueDate,
  onMedicationChange,
  onDirectionChange,
  onIssueDateChange,
  lastIssuedDate,
  onSetAdvancedDate,
  headerAction,
  onRemove,
  isRemoveDisabled
}: Props) {
  const position = index + 1
  // Both card layouts require directions on every treatment, OTC ones included.
  const showNoDirectionBadge = isEditable && !direction
  // Card 0 keeps the test ids the single-treatment layout has always used, so the existing
  // Playwright locators stay unambiguous once sibling cards are on the page.
  const suffix = index === 0 ? '' : `-${position}`

  return (
    <div data-testid={`treatment-card-${position}`} className={CARD_CLASSES}>
      <div className='flex items-center justify-between mb-6'>
        <div className='flex items-center gap-3'>
          <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-[#153CB7] text-sm font-semibold leading-none text-white'>
            {position}
          </span>
          <p className='text-[#1D1D1D] text-base font-semibold leading-[170%]'>Treatment {position}</p>
        </div>

        {headerAction}

        {onRemove && (
          <button
            type='button'
            disabled={!isEditable || isRemoveDisabled}
            onClick={onRemove}
            className='flex items-center gap-1.5 text-sm font-medium leading-[170%] text-red-500 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
          >
            <X className='size-4 shrink-0' strokeWidth={2.5} aria-hidden />
            Remove
          </button>
        )}
      </div>

      <p className='text-[#1D1D1D] text-base font-medium leading-[170%] mb-4'>Select Treatment</p>
      <MedicationCombobox
        isDisabled={!isEditable}
        options={options}
        disabledValues={disabledValues}
        onValueChange={onMedicationChange}
        title='Medication'
        defaultValue={medication?.wooSKU}
      />

      <p className='text-[#1D1D1D] text-base font-medium leading-[170%] my-4'>Treatment Directions</p>
      {showNoDirectionBadge && (
        <div className='mb-4 rounded-2xl border border-custom-neutral-200 bg-[#FFE5E5] flex px-6 py-4 justify-between items-center'>
          <p className='text-[#080F30] text-base font-normal leading-[170%]'>
            This medication doesn't have any directions. Please
            <b>{''} add directions before approving</b> the medication request.
          </p>
        </div>
      )}
      <TextareaWithLabel
        isDisabled={!isEditable}
        value={direction}
        title='Direction'
        onChange={e => onDirectionChange(e.target.value)}
        hasError={showNoDirectionBadge}
      />

      {issueDate && onIssueDateChange && (
        <IssueDateField
          isEditable={isEditable}
          issueDate={issueDate}
          onIssueDateChange={onIssueDateChange}
          lastIssuedDate={lastIssuedDate}
          onSetAdvancedDate={onSetAdvancedDate}
          testIdSuffix={suffix}
        />
      )}
    </div>
  )
}
